import { HTTPRequestMethod } from 'lib-shared-legacy-types';
import { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import type { Action, Dispatch } from 'redux';
import { FormState, reduxForm } from 'redux-form';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import type { AudioSelectPayloadT } from 'components/shared/audio/api/types';
import { audioSelect } from 'components/shared/audio/reducers/audio';
import { FlowType } from 'fable/flowEditor';

import {
  Integration,
  IntegrationModalFormValues,
  Props as IntegrationModalProps,
} from '../../components/modals/integration';
import type {
  FlowNodeIntegrationPayload,
  Variable,
} from '../../components/nodeTypes/integration';
import { ContentType } from '../../components/types';

import { ErrorTypeT, validate } from './validators/integration';

export interface StateProps {
  payload: FlowNodeIntegrationPayload;
  initialValues: IntegrationModalFormValues;
  formValues: { [fieldName: string]: any } | IntegrationModalFormValues;
  flowType: FlowType;
}

export interface DispatchProps {
  audioSelect: (payload: AudioSelectPayloadT) => Dispatch<Action>;
}

interface FormType extends FormState {
  initial?: IntegrationModalFormValues;
  syncErrors?: {
    url: string;
  };
}

const FORM_NAME = 'flowFetchDataForm';

export default (
  flowType: FlowType = FlowType.FLOW_TYPE_CALL,
): FunctionComponent<any> => {
  const mapStateToProps = (
    state: RootReducerStateT,
    props: IntegrationModalProps,
  ): StateProps => {
    const payload = props.node.payload
      ? props.node.payload
      : {
          body: '',
          contentType: ContentType.APPLICATION_JSON,
          headers: {},
          httpRequestType: HTTPRequestMethod.Get,
          nextNodeId: null,
          url: '',
          variables: [],
        };

    const variablesWithLocks: IntegrationModalFormValues['variables'] = [];
    const { descendantNeeds } = props.scopeVars;
    payload.variables.forEach((variable: Variable) => {
      const cantDelete = descendantNeeds.includes(variable.id);
      variablesWithLocks.push({
        ...variable,
        type: 'scope',
        locked: cantDelete,
      });
    });

    const initalHeaders: IntegrationModalFormValues['headers'] = [];
    if (payload.headers) {
      Object.entries(payload.headers).forEach(([key, value]: string[]) => {
        initalHeaders.push({
          key,
          value,
        });
      });
    }

    const {
      announcement,
      headers,
      variables,
      nextNodeId,
      ...payloadFormValues
    } = payload;

    const form: FormType = state.form[FORM_NAME];
    const initialValues: IntegrationModalFormValues = {
      ...payloadFormValues,
      contentType:
        payloadFormValues.contentType || ContentType.APPLICATION_JSON,
      httpRequestType:
        payloadFormValues.httpRequestType || HTTPRequestMethod.Get,
      body: payloadFormValues.body || '',
      variables: variablesWithLocks,
      headers: initalHeaders,
      ...(flowType === FlowType.FLOW_TYPE_CALL &&
        announcement && {
          announcement: {
            author: announcement.file?.author || '',
            url: announcement.file?.url || '',
            label: announcement.file?.filename || '',
          },
        }),
    };

    return {
      payload,
      initialValues,
      formValues: form?.values || initialValues,
      flowType,
    };
  };

  const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return {
      audioSelect: (payload: AudioSelectPayloadT) =>
        dispatch(audioSelect(payload)) as any,
    };
  };

  return connect<
    StateProps,
    DispatchProps,
    IntegrationModalProps,
    RootReducerStateT
  >(
    mapStateToProps,
    mapDispatchToProps,
  )(
    reduxForm<IntegrationModalFormValues, IntegrationModalProps, ErrorTypeT>({
      form: FORM_NAME, // A unique identifier for this form.
      enableReinitialize: true,
      validate,
    })(Integration),
  );
};
